/**
 * Start Node Component
 *
 * Entry point node for workflows.
 * Defines environment variables and enables built-in parameters.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import { useI18n } from '@/i18n';
import type { NodeData, CustomVariable, BuiltInVariables } from '@/types/imageWorkflow';

// Built-in variable labels for display
const getBuiltinLabels = (t: any): Record<keyof BuiltInVariables, string> => ({
  user_id: t('imageWorkflows.builtinVariables.userId.label'),
  user_name: t('imageWorkflows.builtinVariables.userName.label'),
  user_email: t('imageWorkflows.builtinVariables.userEmail.label'),
  execution_id: t('imageWorkflows.builtinVariables.executionId.label'),
  workflow_id: t('imageWorkflows.builtinVariables.workflowId.label'),
  workflow_name: t('imageWorkflows.builtinVariables.workflowName.label'),
  timestamp: t('imageWorkflows.builtinVariables.timestamp.label'),
  date: t('imageWorkflows.builtinVariables.date.label'),
  time: t('imageWorkflows.builtinVariables.time.label'),
});

const StartNodeComponent = ({ id, data, selected }: NodeProps) => {
  const { t } = useI18n();
  const nodeData = data as NodeData;
  const variables = nodeData.variables || [];
  const enabledBuiltins = nodeData.enabled_builtins || ['user_id', 'user_name'];
  const BUILTIN_LABELS = getBuiltinLabels(t);

  return (
    <BaseNode id={id} data={nodeData} type="start" selected={selected} hasTargetHandle={false}>
      <div className="space-y-2">
        {/* Built-in Variables Summary */}
        <div className="text-xs">
          <span className="text-gray-500">{t('imageWorkflows.nodes.start.builtinVariables')} </span>
          {enabledBuiltins.length > 0 ? (
            <span className="text-cyan-700">
              {enabledBuiltins.slice(0, 3).map((key) => BUILTIN_LABELS[key]).join(', ')}
              {enabledBuiltins.length > 3 && ` +${enabledBuiltins.length - 3}`}
            </span>
          ) : (
            <span className="text-gray-400 italic">{t('imageWorkflows.nodes.start.notEnabled')}</span>
          )}
        </div>

        {/* Custom Variables Summary */}
        <div className="text-xs">
          <span className="text-gray-500">{t('imageWorkflows.nodes.start.customVariables')} </span>
          {variables.length > 0 ? (
            <span className="text-cyan-700">
              {variables.slice(0, 2).map((v: CustomVariable) => v.key).join(', ')}
              {variables.length > 2 && ` +${variables.length - 2}`}
            </span>
          ) : (
            <span className="text-gray-400 italic">{t('imageWorkflows.nodes.start.notDefined')}</span>
          )}
        </div>
      </div>
    </BaseNode>
  );
};

export const StartNode = memo(StartNodeComponent);
export default StartNode;
